import { useState } from 'react';

import Badge from '@/components/Badge/Badge';
import Widget from '@/components/Widget/Widget';
import WidgetSettingsModal from '@/features/dashboard/components/WidgetSettingsModal';
import { computeSensorHealth, formatCompactAge, isLowBattery } from '@/features/homeAutomation/lib/freshness';
import type { Sensor } from '@/features/homeAutomation/types/sensor';
import { cn } from '@/lib/cn';

import SensorsDetailModal from './SensorsDetailModal';
import useSensorsWidgetConfig from './useSensorsWidgetConfig';
import useSensorsWidgetData from './useSensorsWidgetData';

function fmtTemp(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${v.toFixed(1)}°`;
}

function fmtHumidity(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${Math.round(v)}%`;
}

export default function SensorsWidget() {
  const config = useSensorsWidgetConfig();
  const { sensors, isPending, isError, error, dataUpdatedAt, refetch } = useSensorsWidgetData(config);
  const [detailOpen, setDetailOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const hasHome = !!config.homeId;
  const status = isPending ? 'loading' : isError ? 'error' : sensors.length === 0 ? 'empty' : 'loaded';

  return (
    <>
      <Widget
        title="Sensors"
        status={status}
        errorMessage={error instanceof Error ? error.message : 'Failed to load sensors.'}
        emptyMessage="No sensors yet — promote one from Unknown sensors in Home settings."
        loadingMessage="Loading sensors…"
        needsSetup={!hasHome}
        setupMessage="Pick a home in widget settings to show its sensors."
        onConfigure={() => setSettingsOpen(true)}
        onOpenDetail={hasHome ? () => setDetailOpen(true) : undefined}
      >
        <ul className="flex h-full flex-col justify-evenly">
          {sensors.map((s) => (
            <SensorRow key={s.id} sensor={s} />
          ))}
        </ul>
      </Widget>

      {hasHome && (
        <SensorsDetailModal
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          sensors={sensors}
          lastUpdated={dataUpdatedAt ? new Date(dataUpdatedAt) : null}
          onRefresh={() => refetch()}
        />
      )}
      <WidgetSettingsModal widgetId="sensors" open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
}

function SensorRow({ sensor }: { sensor: Sensor }) {
  const state = sensor.currentState;
  const health = computeSensorHealth(state);
  const lowBattery = isLowBattery(state?.batteryMv);
  const dimmed = health.tier === 'stale' || health.tier === 'unknown';
  const showAgeBadge = (health.tier === 'aging' || health.tier === 'stale') && health.ageMs != null;

  return (
    <li
      className={cn('grid grid-cols-[1fr_auto_auto] items-center gap-x-3 py-0.5', dimmed && 'opacity-60')}
      aria-label={dimmed ? `${sensor.name}: stale reading` : undefined}
    >
      <span className="flex min-w-0 items-center gap-2">
        <span className="truncate text-[15px] text-(--text-secondary)">{sensor.name}</span>
        {showAgeBadge && (
          <Badge tone={health.tier === 'stale' ? 'danger' : 'warning'} className="px-1.5 py-0 text-[10px] font-medium">
            {formatCompactAge(health.ageMs!)}
          </Badge>
        )}
        {lowBattery && (
          <Badge tone="warning" className="px-1.5 py-0 text-[10px] font-medium">
            low batt
          </Badge>
        )}
      </span>
      <span className="font-display text-lg font-semibold text-(--text-primary) tabular-nums">{fmtTemp(state?.temperatureC ?? null)}</span>
      <span className="font-display min-w-11 text-right text-sm text-(--text-tertiary) tabular-nums">
        {fmtHumidity(state?.humidityPct ?? null)}
      </span>
    </li>
  );
}
